/**
 * <NukkiEditor> — 통짜 에디터 (스키닝 1단계).
 *
 * useNukki() + 기본 마크업. 그게 전부다. 그래서 4단계 스키닝을 다 지원해도
 * 추가 비용이 거의 없다 (설계문서 §6.1):
 *
 *   1단계  <NukkiEditor backend={...} />                     기본값으로 바로 동작
 *   2단계  style={{ '--nk-accent': '#7c3aed' }}              CSS 변수 테마
 *   3단계  classNames={{ toolbar: 'gap-1 bg-zinc-900' }}     Tailwind v3/v4 무관
 *   4단계  useNukki() + <NukkiCanvas />                      완전 커스텀 UI
 */
import { useCallback, useRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { ExportOptions, MaskBackend } from '@nukki/core';
import { useNukki } from './useNukki.js';
import { NukkiCanvas } from './NukkiCanvas.js';
import { resolveMessages, type Locale, type Messages } from './i18n.js';

export type NukkiSlot =
  | 'root' | 'card' | 'fileButton' | 'hint' | 'toolbar' | 'subbar'
  | 'button' | 'primary' | 'segment' | 'stage' | 'busy' | 'error' | 'confirm';

export interface NukkiEditorProps {
  backend: MaskBackend;
  workingSize?: number | undefined;
  exportOptions?: ExportOptions | undefined;
  /**
   * 다운로드 버튼. **기본 false.**
   * WebView 에서 `a[download]` 가 동작하지 않으므로 저장은 호스트가 해야 한다
   * (설계문서 C-2). `onDone` 으로 Blob 을 받아 Capacitor Filesystem 등으로 처리할 것.
   */
  showDownload?: boolean | undefined;
  lang?: Locale | undefined;
  messages?: Partial<Messages> | undefined;
  classNames?: Partial<Record<NukkiSlot, string>> | undefined;
  style?: CSSProperties | undefined;
  maxUploadBytes?: number | undefined;
  /** 완료된 Blob. 저장·업로드는 호스트 책임. */
  onDone?: ((blob: Blob) => void) | undefined;
  onError?: ((err: Error) => void) | undefined;
  /** 툴바 오른쪽에 끼워 넣을 것 (프로젝트별 액션 버튼 등). */
  toolbarExtra?: ReactNode | undefined;
}

const cx = (...v: (string | undefined | false)[]) => v.filter(Boolean).join(' ');

export function NukkiEditor(props: NukkiEditorProps) {
  const {
    backend, workingSize, exportOptions, showDownload = false,
    lang = 'ko', messages, classNames: cn = {}, style,
    maxUploadBytes, onDone, onError, toolbarExtra,
  } = props;

  const t = resolveMessages(lang, messages);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const nk = useNukki({
    backend,
    ...(workingSize !== undefined ? { workingSize } : {}),
    ...(exportOptions !== undefined ? { exportOptions } : {}),
    ...(maxUploadBytes !== undefined ? { maxUploadBytes } : {}),
    ...(onDone !== undefined ? { onDone } : {}),
    ...(onError !== undefined ? { onError } : {}),
  });

  const pick = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (f) void nk.load(f);
  }, [nk]);

  const save = useCallback(async () => {
    let blob: Blob;
    try {
      blob = await nk.exportBlob();
    } catch (e) {
      onError?.(e as Error);
      return;
    }
    if (!showDownload) return;
    // 데스크톱 웹 전용 편의. WebView 에서는 조용히 실패하므로 onDone 이 진짜 경로다.
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'nukki.png';
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }, [nk, showDownload, onError]);

  return (
    <div className={cx('nk-root', cn.root)} style={style}>
      {!nk.loaded && (
        <div className={cx('nk-card', cn.card)}>
          <label className={cx('nk-filebtn', cn.fileButton)}>
            {t.pick}
            <input ref={fileRef} type="file" accept="image/*" onChange={pick} />
          </label>
          <p className={cx('nk-hint', cn.hint)}>{t.hint}</p>
        </div>
      )}

      {nk.loaded && (
        <>
          <div className={cx('nk-bar', cn.toolbar)}>
            <button
              type="button"
              className={cx('nk-btn', 'nk-btn--primary', cn.primary)}
              onClick={() => void nk.autoSelect()}
            >
              {t.auto}
            </button>
            <div className={cx('nk-seg', cn.segment)} role="group" aria-label={t.modeGroup}>
                {([['replace', t.modeReplace], ['add', t.modeAdd], ['subtract', t.modeSubtract]] as const)
                  .map(([m, label]) => (
                    <button
                      key={m}
                      type="button"
                      className={cx('nk-btn', cn.button)}
                      aria-pressed={nk.lassoMode === m}
                      onClick={() => nk.setLassoMode(m)}
                    >
                      {label}
                  </button>
                ))}
            </div>
            <div className={cx('nk-seg', cn.segment)} role="group" aria-label={t.refineGroup}>
              <button
                type="button" className={cx('nk-btn', cn.button)}
                aria-pressed={nk.refine} onClick={() => nk.setRefine(true)}
              >{t.refineOn}</button>
              <button
                type="button" className={cx('nk-btn', cn.button)}
                aria-pressed={!nk.refine} onClick={() => nk.setRefine(false)}
              >{t.refineOff}</button>
            </div>
            <span className="nk-grow" />
            {toolbarExtra}
          </div>

          <div className={cx('nk-subbar', cn.subbar)}>
            <span className="nk-grow" />
            <div className={cx('nk-seg', cn.segment)} role="group" aria-label={t.zoomGroup}>
              <button
                type="button" className={cx('nk-btn', cn.button)}
                onClick={nk.zoomOut} disabled={!nk.canZoomOut} aria-label={t.zoomOut}
              >−</button>
              <span className="nk-zoom" aria-live="polite">
                {Math.round(nk.transform.scale * 100)}%
              </span>
              <button
                type="button" className={cx('nk-btn', cn.button)}
                onClick={nk.zoomIn} disabled={!nk.canZoomIn} aria-label={t.zoomIn}
              >+</button>
            </div>
            {nk.transform.scale !== 1 && (
              <button type="button" className={cx('nk-btn', cn.button)} onClick={nk.resetView}>
                {t.resetView}
              </button>
            )}
            <button
              type="button" className={cx('nk-btn', cn.button)}
              disabled={!nk.canUndo} onClick={nk.undo}
            >{t.undo}</button>
            <button
              type="button" className={cx('nk-btn', cn.button)}
              disabled={!nk.canRedo} onClick={nk.redo}
            >{t.redo}</button>
            <button type="button" className={cx('nk-btn', cn.button)} onClick={nk.reset}>
              {t.reset}
            </button>
          </div>

          <NukkiCanvas nukki={nk} className={cn.stage} label={t.canvasLabel}>
            {nk.busy && (
              <div className={cx('nk-busy', cn.busy)}>
                <div className="nk-spin" />
                <span>{t.analyzing}</span>
              </div>
            )}
          </NukkiCanvas>

          {nk.hasSelection && (
            <div className={cx('nk-confirm', cn.confirm)} role="status">
              <span className="nk-grow">{t.selHint}</span>
              <button
                type="button" className={cx('nk-btn', cn.button)}
                onClick={nk.undoSelectionStep}
              >{t.undoStep}</button>
              <button
                type="button" className={cx('nk-btn', cn.button)}
                onClick={nk.clearSelection}
              >{t.cancelSel}</button>
              <button
                type="button" className={cx('nk-btn', 'nk-btn--primary', cn.primary)}
                onClick={nk.applySelection}
              >{t.applySel}</button>
            </div>
          )}

          <div className={cx('nk-bar', cn.toolbar)}>
            <button
              type="button"
              className={cx('nk-btn', 'nk-btn--primary', 'nk-grow', cn.primary)}
              onClick={() => void save()}
            >
              {t.save}
            </button>
            <label
              className={cx('nk-filebtn', cn.fileButton)}
              style={{ flex: '0 0 auto', padding: '9px 14px', fontSize: 12.5,
                       background: 'var(--nk-panel2)', border: '1px solid var(--nk-line)',
                       color: 'var(--nk-text)' }}
            >
              {t.another}
              <input type="file" accept="image/*" onChange={pick} />
            </label>
          </div>
        </>
      )}

      {nk.error && (
        <div className={cx('nk-err', cn.error)} role="alert">
          <span className="nk-grow">⚠ {nk.error.message}</span>
          {nk.error.retry && (
            <button type="button" className={cx('nk-btn', cn.button)} onClick={nk.error.retry}>
              {t.retry}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
